import React, { useState } from "react";
import { LuEye, LuMail, LuRotateCcw } from "react-icons/lu";
import SendReceiptPopup from "../PatientPaymentDetail/PaymentDetailsInfo/SendReceiptPopup";
import PaymentRefundPopup from "Popups/PaymentRefundPopup/PaymentRefundPopup";
import Show from "Components/Show/Show";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import api from "http";
import Spinner from "Common/Spinner";
import { toast } from "react-toastify";
import { PAYMENT_METHODS } from "Common/Constants";
import ViewPatientStatementPopup from "Popups/ViewPatientStatementPopup/ViewPatientStatementPopup";
import FullPageLoader from "Components/FullPageLoader/FullPageLoader";
import ReactPortal from "Components/ReactPortal/ReactPortal";

const PaymentDetailView = ({ row, onRefresh }) => {
  const [showSendReceiptPopup, setShowSendReceiptPopup] = useState(false);
  const [viewPaymentRefundPopup, setViewPaymentRefundPopup] = useState(false);
  const [loader, setLoader] = useState(false);
  const [openReceiptPopup, setOpenReceiptPopup] = useState(false);
  const [viewReceiptPdf, setViewReceiptPdf] = useState({ type: "", value: "" });
  const disabledRefund = !(
    row.paymentMethod === PAYMENT_METHODS.CREDIT_CARD &&
    row.amount > 0 &&
    row.postedAmount === 0 &&
    !row.isPosted
  );
  const handleViewReceipt = async () => {
    try {
      setLoader(true);
      const isMethodCashOrCheck = [
        PAYMENT_METHODS.CASH,
        PAYMENT_METHODS.CHECK,
      ].includes(row.paymentMethod);
      const paymentId = row.paymentId;
      if (isMethodCashOrCheck) {
        const response = await api.get(
          `${process.env.REACT_APP_TC_PMS}/api/PatientPayment/GeneratePaymentReceipt/${paymentId}`
        );
        const pdfBase64 = response.data.dataModel.base64String;
        setViewReceiptPdf({ type: "base64", value: pdfBase64 });
        setOpenReceiptPopup(true);
      } else {
        const response = await api.get(
          process.env.REACT_APP_TC_PMS +
          `/api/PatientPayment/FindPaymentReceipt/${paymentId}`
        );
        window.open(response.data.dataModel.receiptPresignedUrl, "_blank");
      }
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleSendReceipt = () => {
    setShowSendReceiptPopup(true);
  };

  const handleRefund = () => {
    setViewPaymentRefundPopup(true);
  };

  const handleRefundPayment = async (value) => {
    try {
      setLoader(true);
      const payload = {
        paymentMainId: row.paymentId,
        amount: value.amount,
        reason: value.reason,
        reasonNote: value.reasonNote,
      };
      const response = await api.post(
        `${process.env.REACT_APP_TC_PMS}/api/PatientPayment/RefundPaymentIntent`,
        payload
      );
      setViewPaymentRefundPopup(false);
      toast.success(response.data.responseMessage);
      onRefresh();
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const closePaymentRefundPopup = () => {
    setViewPaymentRefundPopup(false);
  };

  const closeSendReceiptPopup = () => {
    setShowSendReceiptPopup(false);
  };
  const closePaymentReceipt = () => {
    setOpenReceiptPopup(false);
  };
  return (
    <>
      <Show when={loader}>
        <ReactPortal wrapperId="PaymentDetailViewFullPageLoader">
          <FullPageLoader allowFullScreen />
        </ReactPortal>
      </Show>
      <Show when={showSendReceiptPopup}>
        <SendReceiptPopup
          paymentMainId={row.paymentId}
          onClose={closeSendReceiptPopup}
          paidAmount={row.amount}
          receivedDate={row.receivedDate}
          patientInfo={{
            email: row.email,
            phone: row.phonenumber,
          }}
          isCashOrCheck={[PAYMENT_METHODS.CASH, PAYMENT_METHODS.CHECK].includes(
            row.paymentMethod
          )}
        />
      </Show>
      <Show when={openReceiptPopup}>
        <ViewPatientStatementPopup
          Heading="View Payment Receipt"
          onClose={closePaymentReceipt}
          ViewPdf={viewReceiptPdf}
        />
      </Show>
      <Show when={viewPaymentRefundPopup}>
        <PaymentRefundPopup
          required
          paidAmount={Number(row.amount).toFixed(2)}
          onCancel={closePaymentRefundPopup}
          onConfirm={(value) => handleRefundPayment(value)}
        />
      </Show>
      <div className="p-3 space-y-3">
        <div className="grid items-start gap-6 md:grid-cols-4">
          <div className="grid items-start gap-8 md:col-span-3 md:grid-cols-3">
            <div>
              <div className="mb-1 text-sm font-semibold">
                Transaction Details
              </div>
              <div className="text-sm text-slate-600">
                Payment - Invoice #{row.paymentId || ""}
              </div>
            </div>

            <div>
              <div className="mb-1 text-sm font-semibold">Payment Method</div>
              <div className="text-sm text-slate-600">
                {row.paymentMethod || ""}
              </div>
            </div>

            <div className="max-w-xs">
              <div className="mb-1 text-sm font-semibold">Memo</div>
              <div
                className="text-sm truncate cursor-pointer text-slate-600"
                data-tooltip-id="paymentDetailMemo_tooltip"
                data-tooltip-content={row.memo || ""}
              >
                {row.memo || ""}
              </div>
            </div>
          </div>

          {/* Right Section - Action Buttons */}

          <div className="flex items-center gap-2 md:justify-self-end">
            <button
              disabled={loader}
              onClick={handleViewReceipt}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loader ? <Spinner /> : <LuEye className="w-3.5 h-3.5" />}
              View Receipt
            </button>

            <button
              onClick={handleSendReceipt}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <LuMail className="w-3.5 h-3.5" />
              Send Receipt
            </button>
            {![PAYMENT_METHODS.CHECK, PAYMENT_METHODS.CASH].includes(
              row.paymentMethod
            ) && (
              <button
                disabled={loader || disabledRefund}
                onClick={handleRefund}
                data-tooltip-id={
                  disabledRefund ? "refundDisabled_tooltip" : undefined
                }
                data-tooltip-content={
                  disabledRefund
                    ? "Posted payment cannot be refunded. Apply a payment reversal first, then issue the refund"
                    : undefined
                }
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loader ? (
                  <Spinner />
                ) : (
                  <LuRotateCcw className="w-3.5 h-3.5" />
                )}
                Refund
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentDetailView;
